import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useFormChanged, zodResolver } from "@/lib/form";
import { singleInviteDomain } from "@/lib/invitation-csv";
import { useCreateUser, useDomains, useUpdateUser } from "@/lib/queries";
import {
  DISPLAY_NAME_MAX, EXTENSION_RE, ROLE_DESCRIPTIONS, ROLE_LABELS, assignableRoles, editsOwnPhone, type AppUser,
  type Role,
} from "@/lib/types";
import { generatePassphrase } from "@/lib/passphrase";
import { ownVoiceLine } from "@/lib/own-voice";
import { useDeleteUserVoice } from "@/lib/own-voice-api";
import { formatDateTime } from "@/lib/utils";
import { PERSON_VOICE_OPTIONS } from "@/lib/voice-settings";

/**
 * Create / edit a user, and with them the phone that is theirs.
 *
 * The scope sent mirrors `rbac.valid_scope_for_role` on the server: a superadmin has no tenant or
 * domain, an admin has a tenant but no domain, and the two lower roles need both. The domain is not
 * asked for (ACL-553): a tenant has one, so the form puts the account in it as the invite form does
 * (`singleInviteDomain`), and an admin made here gets their phone there too. "Domain", "the SIP realm
 * their phone registers in", was a question nobody adding a rep could answer.
 *
 * The phone is here and not on a dialog of its own because it is not a separate thing any more:
 * an account is created with its endpoint and keeps it for life, so what is left to edit is the
 * extension and whether it records. There is no picker for *which* endpoint, deliberately — a
 * call leg names a rep by realm and number and by nothing else, so an endpoint that changed
 * hands would hand its new holder the last holder's call history (ADR-012 in `platform`).
 */

const MIN_PASSWORD = 12;

const schema = z
  .object({
    /** Their real name (ADR-172 in `platform`). Required in the form itself, which knows whether
     *  the account is new: an account from before ADR-172 may have none, and an edit of its role
     *  or password must not wait on somebody typing one. */
    name: z.string().trim().max(200),
    /** The name they go by. Left empty, the server stores their name. */
    display_name: z.string().max(DISPLAY_NAME_MAX, `At most ${DISPLAY_NAME_MAX} characters`).optional(),
    email: z.string().min(1, "Required").email("That does not look like an email"),
    role: z.enum(["superadmin", "admin", "supervisor", "rep"]),
    tenant_id: z.number().nullable(),
    domain_id: z.number().nullable(),
    /** An admin being edited who has no phone yet gets one in the tenant's domain. A new admin
     *  always does, as an invited one does. A superadmin gets none from this form, as from an
     *  invitation. */
    give_phone: z.boolean(),
    extension: z.string(),
    record_calls: z.boolean(),
    is_active: z.boolean(),
    /** The voice that speaks in their name: "" is not set, the tenant's default. */
    voice_gender: z.enum(["", "female", "male"]),
    password: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.role !== "superadmin" && value.role !== "admin" && value.domain_id === null) {
      ctx.addIssue({
        code: "custom",
        path: ["domain_id"],
        message: `There is no domain to put a ${ROLE_LABELS[value.role].toLowerCase()} in. A superadmin sets one up under Domains.`,
      });
    }
    if (value.extension && !EXTENSION_RE.test(value.extension)) {
      ctx.addIssue({
        code: "custom",
        path: ["extension"],
        message: "Exactly four digits, for example 1004",
      });
    }
    if (value.password && value.password.length < MIN_PASSWORD) {
      ctx.addIssue({
        code: "custom",
        path: ["password"],
        message: `At least ${MIN_PASSWORD} characters`,
      });
    }
  });

type FormValues = z.infer<typeof schema>;

export function UserFormDialog({
  open, onOpenChange, user,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  user?: AppUser;
}) {
  const editing = Boolean(user);
  const { user: actor } = useAuth();
  const toast = useToast();
  const create = useCreateUser();
  const update = useUpdateUser();
  const { data: domains } = useDomains();
  const [formError, setFormError] = React.useState<string | null>(null);

  const roles = assignableRoles(actor?.role);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "", display_name: "", email: "", role: (roles.at(-1) ?? "rep") as Role,
      tenant_id: actor?.tenant_id ?? null, domain_id: null, give_phone: false,
      extension: "", record_calls: true, is_active: true, voice_gender: "", password: "",
    },
  });
  // Measured from what the reset below loads, so a new user's generated password is not a change,
  // and nor is the domain the form fills in by itself.
  const changed = useFormChanged(form, ["domain_id"]);

  const role = form.watch("role");
  const needsDomain = role === "supervisor" || role === "rep";
  const hasPhone = Boolean(user?.sip_endpoint_id);
  const soleDomain = singleInviteDomain(domains ?? []);
  // Where a phone made with this save goes: the account's domain for the domain-locked roles, and
  // the tenant's for an admin (always when new, on request when edited). Null means no phone is
  // made, and the extension field has nothing to name.
  const endpointDomain = role === "admin" && (!editing || form.watch("give_phone")) ? soleDomain : null;
  const phoneDomainId = needsDomain ? form.watch("domain_id") : endpointDomain;
  const canHavePhone = hasPhone || phoneDomainId !== null;

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    form.reset(
      user
        ? {
            // An account from before ADR-172 with no real name shows the name it went by.
            name: user.name ?? user.display_name ?? "",
            display_name: user.display_name ?? "",
            email: user.email,
            role: user.role,
            tenant_id: user.tenant_id,
            domain_id: user.domain_id,
            give_phone: false,
            extension: user.sip_endpoint_extension ?? "",
            record_calls: user.sip_endpoint_record_calls ?? true,
            is_active: user.is_active,
            voice_gender: user.voice_gender ?? "",
            password: "",
          }
        : {
            name: "", display_name: "", email: "", role: (roles.at(-1) ?? "rep") as Role,
            tenant_id: actor?.tenant_id ?? null, domain_id: null, give_phone: false,
            extension: "", record_calls: true, is_active: true, voice_gender: "" as const,
            password: generatePassphrase(),
          },
    );
    // `roles` is derived from the actor and stable for a session; including it would reset the
    // form on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, actor?.tenant_id]);

  // The domain follows the role: cleared for one that must not carry it, so a promotion cannot
  // submit a scope the server will reject, and the tenant's for one that needs it. An account that
  // has a domain keeps its own.
  React.useEffect(() => {
    if (!open) return;
    const current = form.getValues("domain_id");
    if (!needsDomain && current !== null) form.setValue("domain_id", null);
    if (needsDomain && current === null && soleDomain !== null) form.setValue("domain_id", soleDomain);
  }, [open, needsDomain, soleDomain, form, user]);

  const renaming = Boolean(
    hasPhone && form.watch("extension") && form.watch("extension") !== user?.sip_endpoint_extension,
  );

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    // A new account needs a real name, and so does one that has one: clearing it is not an edit.
    if (!values.name.trim() && (!editing || Boolean(user?.name))) {
      form.setError("name", { message: "Required" });
      return;
    }
    const extension = values.extension.trim();
    const payload = {
      name: values.name.trim() || null,
      display_name: values.display_name?.trim() || null,
      email: values.email,
      role: values.role,
      // A superadmin belongs to no tenant, so an account made one leaves the tenant it had. Read
      // off the role here rather than cleared in the form: a role picked and then picked back
      // before saving still sends the account's own tenant.
      tenant_id: values.role === "superadmin" ? null : values.tenant_id,
      domain_id: values.domain_id,
      is_active: values.is_active,
      // Only when there is somewhere for a phone to be. Sent without one the server refuses,
      // correctly — it has no realm to create the endpoint in.
      ...(canHavePhone && extension ? { extension } : {}),
      ...(!hasPhone && endpointDomain !== null ? { endpoint_domain_id: endpointDomain } : {}),
      ...(hasPhone ? { record_calls: values.record_calls } : {}),
      ...(values.password ? { password: values.password } : {}),
      // Sent only when it differs from what the account has: a form that did not touch it has
      // nothing to say about it. Null puts the person back on the tenant's default voice.
      ...(values.voice_gender !== (user?.voice_gender ?? "")
        ? { voice_gender: values.voice_gender === "" ? null : values.voice_gender }
        : {}),
    };
    try {
      if (user) {
        await update.mutateAsync({ id: user.id, ...payload });
        toast.success("User updated", values.email);
      } else {
        const created = await create.mutateAsync(payload);
        toast.success(
          "User created",
          created.sip_endpoint_extension
            ? `${values.email} — extension ${created.sip_endpoint_extension}`
            : values.email,
        );
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the user.");
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{editing ? "Edit user" : "New user"}</DialogTitle>
          <DialogDescription>
            {editing
              ? "A new role takes effect without signing them out. A new password signs them out of every session."
              : "They get a phone with their account, and can sign in as soon as you save."}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Name"
                htmlFor="name"
                required
                hint="Their real name."
                error={form.formState.errors.name?.message}
              >
                <Input
                  autoComplete="off"
                  invalid={Boolean(form.formState.errors.name)}
                  {...form.register("name")}
                />
              </Field>
              <Field
                label="Display name"
                htmlFor="display_name"
                hint={
                  // A display name filled from the real name once does not follow it (ADR-172 in
                  // `platform`), so an admin correcting a typo in Name is told it stays.
                  editing
                    ? "The name they go by, on their calls and voicemail greetings. Changing Name does not change it. They can change it under Account."
                    : "The name they go by, on their calls and voicemail greetings. Left empty, it is their name. They can change it under Account."
                }
                error={form.formState.errors.display_name?.message}
              >
                <Input autoComplete="off" {...form.register("display_name")} />
              </Field>
            </div>

            <Field label="Email" htmlFor="email" required error={form.formState.errors.email?.message}>
              <Input
                type="email"
                autoComplete="off"
                invalid={Boolean(form.formState.errors.email)}
                {...form.register("email")}
              />
            </Field>

            <Field
              label="Role"
              htmlFor="role"
              required
              hint={ROLE_DESCRIPTIONS[role]}
              error={form.formState.errors.role?.message}
            >
              <Select
                value={role}
                onValueChange={(v) => form.setValue("role", v as Role, { shouldValidate: true })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r} value={r} description={ROLE_DESCRIPTIONS[r]}>
                      {ROLE_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {form.formState.errors.domain_id && (
              <Callout tone="danger">{form.formState.errors.domain_id.message}</Callout>
            )}

            <Field
              label="Voicemail voice"
              htmlFor="voice_gender"
              hint="The voice that speaks a voicemail greeting in their name. They can also set it themselves, under Account."
            >
              <Select
                // Radix has no empty value: "unset" stands for it here and nowhere else.
                value={form.watch("voice_gender") || "unset"}
                onValueChange={(v) =>
                  form.setValue("voice_gender", v === "unset" ? "" : (v as "female" | "male"), { shouldDirty: true })
                }
              >
                <SelectTrigger id="voice_gender">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERSON_VOICE_OPTIONS.map((option) => (
                    <SelectItem key={option.value || "unset"} value={option.value || "unset"} description={option.description}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {user?.own_voice && <TheirOwnVoice key={user.own_voice.created_at} user={user} />}

            <PhoneSection
              form={form}
              editing={editing}
              hasPhone={hasPhone}
              offerPhone={editing && !hasPhone && role === "admin" && soleDomain !== null}
              canHavePhone={canHavePhone}
              renaming={renaming}
              currentDomainName={user?.sip_endpoint_domain_name ?? null}
            />

            <Field
              label={editing ? "Set a new password" : "Password"}
              htmlFor="password"
              required={!editing}
              hint={
                editing
                  ? "Leave blank to keep the current password."
                  : `At least ${MIN_PASSWORD} characters. Share it over something other than email.`
              }
              error={form.formState.errors.password?.message}
              action={
                <button
                  type="button"
                  className="text-xs text-accent-11 hover:underline"
                  onClick={() =>
                    form.setValue("password", generatePassphrase(), { shouldValidate: true })
                  }
                >
                  Generate
                </button>
              }
            >
              <Input
                // `text`, not `password`: whoever creates the account has to read this out or
                // paste it somewhere, and a masked field they cannot check is where typos and
                // support tickets come from.
                type="text"
                autoComplete="new-password"
                className="font-mono"
                invalid={Boolean(form.formState.errors.password)}
                {...form.register("password")}
              />
            </Field>

            {editing && (
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                <div>
                  <p className="text-xs font-medium text-text">Active</p>
                  <p className="text-xs text-text-muted">
                    Turning this off signs them out immediately and blocks new logins.
                  </p>
                </div>
                <Switch
                  checked={form.watch("is_active")}
                  onCheckedChange={(v) => form.setValue("is_active", v)}
                  aria-label="Active"
                />
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={form.formState.isSubmitting}>
              {editing ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The account's phone: where it lives, what it is called, and whether it records.
 *
 * Separated out because it is the one part of this form whose fields appear and disappear on
 * two independent conditions — whether the account already has a phone, and whether its role
 * needs somewhere to be told to put one — and reading that inline was three nested ternaries.
 */
function PhoneSection({
  form, editing, hasPhone, offerPhone, canHavePhone, renaming, currentDomainName,
}: {
  form: ReturnType<typeof useForm<FormValues>>;
  editing: boolean;
  hasPhone: boolean;
  /** An admin with no phone yet, who can be given one. */
  offerPhone: boolean;
  canHavePhone: boolean;
  renaming: boolean;
  currentDomainName: string | null;
}) {
  // A phone made with this save gets the next free extension, which is what nearly everybody
  // wants, so the box for choosing one is behind a link (ACL-553). It stays open once there is
  // something in it to see: a number typed, or the reason it was refused.
  const [assigning, setAssigning] = React.useState(false);
  const typed = form.watch("extension") !== "" || Boolean(form.formState.errors.extension);
  const showExtension = hasPhone || (canHavePhone && (assigning || typed));
  return (
    <div className="space-y-4 rounded-md border border-border p-3">
      <div>
        <p className="text-xs font-medium text-text">Phone</p>
        <p className="text-xs text-text-muted">
          {hasPhone
            ? `Their endpoint on ${currentDomainName ?? "the switch"}. It is theirs for as long as the account exists.`
            : editing
              ? "This account has no phone yet."
              : "Created with the account. Nobody else can be given it."}
        </p>
      </div>

      {offerPhone && (
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-medium text-text">Give them a phone</p>
            <p className="text-xs text-text-muted">
              For an admin who takes calls. Leave it off for one who never does.
            </p>
          </div>
          <Switch
            checked={form.watch("give_phone")}
            onCheckedChange={(v) => form.setValue("give_phone", v, { shouldDirty: true })}
            aria-label="Give them a phone"
          />
        </div>
      )}

      {!showExtension && canHavePhone && (
        <p className="text-xs text-text-muted">
          They get the next free extension.{" "}
          <button
            type="button"
            className="font-medium text-accent-11 hover:underline"
            onClick={() => setAssigning(true)}
          >
            Assign extension
          </button>
        </p>
      )}

      {showExtension && (
        <Field
          label="Extension"
          htmlFor="extension"
          hint={
            hasPhone
              ? "Four digits. What colleagues dial, and what their softphone signs in as."
              : "Four digits. Leave blank for the next free number in the domain."
          }
          error={form.formState.errors.extension?.message}
        >
          <Input
            className="font-mono"
            autoComplete="off"
            inputMode="numeric"
            placeholder="1004"
            // Opened by the link: the rep's next keypress is the number.
            autoFocus={!hasPhone}
            invalid={Boolean(form.formState.errors.extension)}
            {...form.register("extension")}
          />
        </Field>
      )}

      {renaming && (
        <Callout tone="warning">
          Changing the extension signs their softphone back in on the new number, and any desk
          phone on the old one stops until it is reconfigured. Calls they made before the change
          stay filed under the old number.
        </Callout>
      )}

      {hasPhone && (
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-medium text-text">Record calls</p>
            <p className="text-xs text-text-muted">
              {/* Both directions, because that is the part people get wrong: the toggle is not
                  "record what they dial", it is "record this phone". */}
              This phone's own side of calls it places and calls it is rung for. Takes effect on
              the next call
              {/* Only an admin's own Account page has this switch. For anybody below, this form
                  is the one place it can be changed, and the hint must not send them looking. */}
              {editsOwnPhone(form.watch("role"))
                ? " — and as an admin they can also change it in their own settings."
                : "."}
            </p>
          </div>
          <Switch
            checked={form.watch("record_calls")}
            onCheckedChange={(v) => form.setValue("record_calls", v)}
            aria-label="Record calls"
          />
        </div>
      )}
    </div>
  );
}

/**
 * A person's own voice, on their account as an admin sees it (ADR-117 in `platform`): that they
 * have one, and the way to delete it. An admin never makes one: the recording and the agreement are
 * the person's own, on their Account page. Deleting saves at once, apart from the form, because it
 * also deletes the voice at ElevenLabs and cannot be taken back with Cancel.
 */
function TheirOwnVoice({ user }: { user: AppUser }) {
  const toast = useToast();
  const remove = useDeleteUserVoice();
  const [asking, setAsking] = React.useState(false);
  // The form holds the account as it was when it was opened, so a voice deleted here is
  // remembered here: the line goes at once, not when the form is opened again.
  const [deleted, setDeleted] = React.useState(false);
  if (!user.own_voice || deleted) return null;
  const who = user.display_name || user.email;

  const erase = async () => {
    try {
      await remove.mutateAsync(user.id);
      setAsking(false);
      setDeleted(true);
      toast.success("Their voice was deleted", `${who} is spoken for by the team's voice again.`);
    } catch (e) {
      toast.error("Could not delete their voice", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2">
      <p className="min-w-0 text-xs text-text-muted">
        <span className="font-medium text-text">Speaks in their own voice.</span>{" "}
        {ownVoiceLine(user.own_voice, formatDateTime)} It is used instead of the voice above.
      </p>
      <Button type="button" variant="outline" size="sm" onClick={() => setAsking(true)}>
        Delete their voice
      </Button>
      <ConfirmDialog
        open={asking}
        onOpenChange={setAsking}
        title={`Delete ${who}'s voice?`}
        description="It stops speaking their greetings at once and is deleted at ElevenLabs. The team's voice speaks for them again. Only they can record a new one, on their Account page."
        confirmLabel="Delete their voice"
        destructive
        loading={remove.isPending}
        onConfirm={() => void erase()}
      />
    </div>
  );
}
